/**
 * Characteristic load cases for reporting (snow / wind / permanent).
 * Shared by TrussLoadingsView and print report.
 */

function fmtNum(v, d = 3) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(d) : '—';
}

function topChordSegments(geometry) {
  if (!geometry?.nodes?.length) return { nodes: [], nSeg: 0 };
  const topRoles = (geometry.nodes || []).some((n) => n.role === 'arch_rib')
    ? new Set(['arch_rib'])
    : new Set(['top_chord', 'top_chord_left', 'top_chord_right']);
  const nodes = geometry.nodes
    .filter((n) => topRoles.has(n.role))
    .sort((a, b) => a.x - b.x);
  return { nodes, nSeg: Math.max(nodes.length - 1, 0) };
}

function bottomChordSegments(geometry) {
  if (!geometry?.nodes?.length) return { nodes: [], nSeg: 0 };
  const nodes = geometry.nodes
    .filter((n) => n.role === 'bottom_chord')
    .sort((a, b) => a.x - b.x);
  return { nodes, nSeg: Math.max(nodes.length - 1, 0) };
}

function pushUniformTop(cases, id, label, q, nSeg) {
  const value = Number(q);
  if (!(Math.abs(value) > 1e-9) || nSeg <= 0) return;
  cases.push({
    id,
    label,
    kind: 'roof',
    roofSegQ: Array(nSeg).fill(value),
    colLeftQ: 0,
    colRightQ: 0,
    detail: { q_kN_m: value },
  });
}

/** Uniform snow line load when EN shape coefficients are not on the result. */
function uniformSnowLineLoad(inputs, snowMeta) {
  const building = !!inputs?.building_mode;
  if (!building) {
    const q = Number(inputs?.snow_kN_m);
    if (Number.isFinite(q)) return q;
  }
  const fromMeta = Number(snowMeta?.snow_kN_m ?? snowMeta?.snow_kN_m_uniform);
  return Number.isFinite(fromMeta) ? fromMeta : 0;
}

function boundsFromGeometry(geom) {
  if (!geom?.nodes?.length) return { minX: 0, maxX: 10, minY: -2, maxY: 3, w: 10, h: 5 };
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  geom.nodes.forEach((n) => {
    minX = Math.min(minX, n.x);
    maxX = Math.max(maxX, n.x);
    minY = Math.min(minY, n.y);
    maxY = Math.max(maxY, n.y);
  });
  const pad = Math.max(maxX - minX, maxY - minY) * 0.32 || 0.8;
  return {
    minX: minX - pad,
    maxX: maxX + pad,
    minY: minY - pad,
    maxY: maxY + pad,
    w: maxX - minX + 2 * pad,
    h: maxY - minY + 2 * pad,
  };
}

function segmentOutwardNormal(ax, ay, bx, by) {
  const dx = bx - ax;
  const dy = by - ay;
  const len = Math.hypot(dx, dy) || 1;
  let nx = -dy / len;
  let ny = dx / len;
  if (ny < 0) {
    nx = -nx;
    ny = -ny;
  }
  return { nx, ny, len };
}

export function windMetaFromResults(results) {
  return results?.wind_meta || results?.wind_loads?.wind_meta || {};
}

export function resolveLoadingCases(results, inputs) {
  const cases = [];
  const windMeta = windMetaFromResults(results);
  const snowMeta = results?.snow_meta || {};
  const windCases = results?.wind_loads?.wind_cases || [];
  const { nSeg } = topChordSegments(results?.geometry || {});

  const snowSegs = snowMeta.roof_top_k_per_seg_k;
  if (Array.isArray(snowSegs) && snowSegs.length > 0) {
    cases.push({
      id: 'snow',
      label: snowMeta.shape === 'dual' ? 'Snow (duo Case I)' : 'Snow (characteristic)',
      kind: 'roof',
      roofSegQ: snowSegs.map(Number),
      colLeftQ: 0,
      colRightQ: 0,
      detail: snowMeta,
      regionLabels: snowMeta.side_per_seg || [],
    });
    const archArrangements = Array.isArray(snowMeta.snow_arrangement_details)
      ? snowMeta.snow_arrangement_details.filter((a) => a && a.name && a.name !== 'balanced')
      : [];
    archArrangements.forEach((arr) => {
      if (!Array.isArray(arr.roof_top_k_per_seg_k) || !arr.roof_top_k_per_seg_k.length) return;
      cases.push({
        id: `snow_${arr.name}`,
        label: `Snow (${arr.name})`,
        kind: 'roof',
        roofSegQ: arr.roof_top_k_per_seg_k.map(Number),
        colLeftQ: 0,
        colRightQ: 0,
        detail: { ...snowMeta, ...arr },
        regionLabels: arr.side_per_seg || [],
      });
    });
    const caseIi = snowMeta.snow_dual_case_ii;
    if (caseIi?.roof_top_k_per_seg_k?.length) {
      cases.push({
        id: 'snow_case_ii',
        label: 'Snow (duo Case II)',
        kind: 'roof',
        roofSegQ: caseIi.roof_top_k_per_seg_k.map(Number),
        colLeftQ: 0,
        colRightQ: 0,
        detail: { ...snowMeta, ...caseIi },
        regionLabels: caseIi.side_per_seg || [],
      });
    }
  }

  if (!cases.some((c) => c.id === 'snow')) {
    pushUniformTop(cases, 'snow', 'Snow (characteristic)', uniformSnowLineLoad(inputs, snowMeta), nSeg);
  }

  windCases.forEach((wc) => {
    const name = String(wc?.name || '');
    if (name !== 'wind_long' && !name.startsWith('wind_trans')) return;
    const segs = wc.roof_top_k_per_seg_k;
    const axis = wc.axis || (name === 'wind_trans' ? 'Y' : 'X');
    const label =
      wc.label ||
      (axis === 'X' ? 'Wind X' : 'Wind Y') ||
      (name === 'wind_trans' ? 'Wind Y' : 'Wind X');
    const axisNote =
      axis === 'X'
        ? 'along building length (side-wall suction A/B/C)'
        : 'along span (D windward / E leeward)';
    cases.push({
      id: name,
      label: `${label} (${axisNote})`,
      shortLabel: label,
      axis,
      kind: 'wind',
      roofSegQ: Array.isArray(segs) ? segs.map(Number) : [],
      colLeftQ: Number(wc.col_left_q_kN_m) || 0,
      colRightQ: Number(wc.col_right_q_kN_m) || 0,
      detail: wc,
      regionLabels: (wc.roof_zones_per_seg || []).map((r) => r.debug || ''),
      frameZones: Array.isArray(wc.frame_zones) ? wc.frame_zones : [],
    });
  });

  const building = !!inputs?.building_mode;
  const spacing = Math.max(Number(inputs?.frame_spacing_m) || 6, 0.1);
  const g = building && inputs?.g_kN_m2 != null && inputs.g_kN_m2 !== ''
    ? Number(inputs.g_kN_m2 || 0) * spacing
    : Number(inputs?.g_kN_m || 0);
  if (g > 1e-9 && nSeg > 0) {
    cases.push({
      id: 'permanent',
      label: 'Permanent (g)',
      kind: 'roof',
      roofSegQ: Array(nSeg).fill(g),
      colLeftQ: 0,
      colRightQ: 0,
      detail: { g_kN_m: g },
    });
  }

  const imposed = building
    ? Number(inputs?.imposed_kN_m || 0) || Number(inputs?.imposed_kN_m2 || 0) * spacing
    : Number(inputs?.imposed_kN_m || 0);
  pushUniformTop(cases, 'imposed', 'Imposed', imposed, nSeg);

  const qBot = Number(inputs?.bottom_chord_kN_m || 0);
  const { nSeg: nBot } = bottomChordSegments(results?.geometry || {});
  if (Math.abs(qBot) > 1e-9 && nBot > 0) {
    cases.push({
      id: 'bottom',
      label: 'Bottom chord',
      kind: 'bottom',
      roofSegQ: [],
      bottomSegQ: Array(nBot).fill(qBot),
      colLeftQ: 0,
      colRightQ: 0,
      detail: { bottom_chord_kN_m: qBot },
    });
  }

  return { cases, windMeta, snowMeta };
}

function fmtFactor(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '0';
  const r = Math.round(n * 100) / 100;
  if (Math.abs(r - Math.round(r)) < 1e-9) return String(Math.round(r));
  return r.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
}

function snowActionPresent(inputs, results) {
  const sm = results?.snow_meta || {};
  const segs = sm.roof_top_k_per_seg_k;
  if (Array.isArray(segs) && segs.some((v) => Math.abs(Number(v) || 0) > 1e-9)) return true;
  return Math.abs(uniformSnowLineLoad(inputs, sm)) > 1e-9;
}

function windActionPresent(inputs, results) {
  const windCases = results?.wind_loads?.wind_cases || [];
  const fromCases = windCases.some((wc) => {
    if (Math.abs(Number(wc?.col_left_q_kN_m) || 0) > 1e-9) return true;
    if (Math.abs(Number(wc?.col_right_q_kN_m) || 0) > 1e-9) return true;
    if (Math.abs(Number(wc?.roof_top_uniform_k) || 0) > 1e-9) return true;
    return (wc?.roof_top_k_per_seg_k || []).some((s) => Math.abs(Number(s) || 0) > 1e-9);
  });
  if (fromCases) return true;
  return (
    Math.abs(Number(inputs?.wind_kN_m) || 0) > 1e-9 ||
    Math.abs(Number(inputs?.wind_column_left_kN_m) || 0) > 1e-9 ||
    Math.abs(Number(inputs?.wind_column_right_kN_m) || 0) > 1e-9 ||
    Math.abs(Number(inputs?.wind_manual_column_kN_m) || 0) > 1e-9 ||
    Math.abs(Number(inputs?.wind_manual_top_chord_kN_m) || 0) > 1e-9
  );
}

/**
 * Governing ULS/SLS combination as partial factors, e.g. "ULS: 1.5*snow + 1.15*G".
 * Actions with a zero characteristic value are left out.
 */
export function formatGoverningCombination(results, inputs) {
  const combo = results?.loads_combo;
  if (!combo) return '';
  const building = !!inputs?.building_mode;
  const spacing = Math.max(Number(inputs?.frame_spacing_m) || 6, 0.1);
  const gMag = building && inputs?.g_kN_m2 != null && inputs.g_kN_m2 !== ''
    ? Number(inputs.g_kN_m2 || 0) * spacing
    : Number(inputs?.g_kN_m || 0);
  const imposedMag = building
    ? Number(inputs?.imposed_kN_m || 0) || Number(inputs?.imposed_kN_m2 || 0) * spacing
    : Number(inputs?.imposed_kN_m || 0);
  const named = [
    ['snow', combo.snow, snowActionPresent(inputs, results)],
    ['imposed', combo.imposed, Math.abs(imposedMag) > 1e-9],
    ['wind', combo.wind, windActionPresent(inputs, results)],
    ['G', combo.g, Math.abs(gMag) > 1e-9],
    ['bottom', combo.bottom, Math.abs(Number(inputs?.bottom_chord_kN_m) || 0) > 1e-9],
  ];
  const terms = named
    .filter(([, factor, present]) => present && Math.abs(Number(factor) || 0) > 1e-9)
    .sort((a, b) => Math.abs(Number(b[1])) - Math.abs(Number(a[1])));
  if (!terms.length) {
    return combo.name ? String(combo.name).replace(/_/g, ' ') : '';
  }
  const kind = String(combo.name || '').startsWith('SLS') ? 'SLS' : 'ULS';
  const body = terms
    .map(([label, factor]) => {
      const n = Number(factor);
      if (n < 0) return `-${fmtFactor(Math.abs(n))}*${label}`;
      return `${fmtFactor(n)}*${label}`;
    })
    .join(' + ')
    .replace(/\+ -/g, '− ');
  return `${kind}: ${body}`;
}

export function buildBasisRows(results, inputs, windMeta, snowMeta) {
  const rows = [];
  const wm = windMeta || windMetaFromResults(results);
  const sm = snowMeta || results?.snow_meta || {};
  if (wm.terrain_category != null) rows.push({ label: 'Terrain category', value: wm.terrain_category });
  if (wm.vb0_ms != null) rows.push({ label: 'v_b,0 (m/s)', value: fmtNum(wm.vb0_ms, 2) });
  if (wm.z_e_m != null) rows.push({ label: 'z_e (m)', value: fmtNum(wm.z_e_m, 2) });
  if (wm.c_e != null) rows.push({ label: 'c_e(z_e)', value: fmtNum(wm.c_e, 4) });
  if (wm.k_r != null) rows.push({ label: 'k_r', value: fmtNum(wm.k_r, 4) });
  if (wm.kr_source) rows.push({ label: 'k_r source', value: wm.kr_source });
  if (wm.qp_kN_m2 != null) rows.push({ label: 'q_p (kN/m²)', value: fmtNum(wm.qp_kN_m2, 4) });
  if (wm.e_m != null) rows.push({ label: 'e (m)', value: fmtNum(wm.e_m, 2) });
  if (wm.roof_shape) rows.push({ label: 'Roof shape (wind)', value: wm.roof_shape });
  if (wm.axis_convention) rows.push({ label: 'Building axes', value: wm.axis_convention });
  if (wm.building_length_m != null) rows.push({ label: 'Building length (m)', value: fmtNum(wm.building_length_m, 2) });
  if (wm.num_frames != null) rows.push({ label: 'Num frames', value: String(wm.num_frames) });
  if (sm.sk_kN_m2 != null) rows.push({ label: 's_k (kN/m²)', value: fmtNum(sm.sk_kN_m2, 3) });
  if (sm.snow_Ce != null) rows.push({ label: 'C_e (snow)', value: fmtNum(sm.snow_Ce, 2) });
  if (sm.snow_Ct != null) rows.push({ label: 'C_t (snow)', value: fmtNum(sm.snow_Ct, 2) });
  if (sm.shape) rows.push({ label: 'Roof shape (snow)', value: sm.shape });
  if (inputs?.terrain_category && !rows.some((r) => r.label === 'Terrain category')) {
    rows.push({ label: 'Terrain category', value: inputs.terrain_category });
  }
  if (inputs?.wind_speed_ms != null && !rows.some((r) => r.label.startsWith('v_b'))) {
    rows.push({ label: 'v_b,0 input (m/s)', value: fmtNum(inputs.wind_speed_ms, 2) });
  }
  return rows;
}

/** Wall zone letters for the active wind case / frame (left = windward or near-side). */
export function wallZoneLettersForLoadCase(loadCase) {
  const d = loadCase?.detail || {};
  const axisX = loadCase?.axis === 'X' || loadCase?.id === 'wind_long';
  const wallParts = d.wall_parts || loadCase?.wallParts;
  if (Array.isArray(wallParts) && wallParts.length) {
    const letters = [];
    wallParts.forEach((p) => {
      const z = String(p.zone || '').toUpperCase();
      if (z && !letters.includes(z)) letters.push(z);
    });
    if (letters.length) {
      if (axisX) {
        const joined = letters.join('/');
        return { left: joined, right: joined };
      }
      return { left: letters[0] || 'D', right: letters[1] || letters[0] || 'E' };
    }
  }
  const wz = d.wall_zones;
  if (Array.isArray(wz) && wz.length) {
    const left = String(wz[0]?.zone || '').toUpperCase() || (axisX ? 'A' : 'D');
    const right = String(wz[1]?.zone || wz[0]?.zone || '').toUpperCase() || (axisX ? 'A' : 'E');
    if (axisX) {
      return { left, right: left };
    }
    return { left, right };
  }
  if (axisX) {
    return { left: 'A', right: 'A' };
  }
  return { left: 'D', right: 'E' };
}

/** Zone letters present on this case (walls + roof segments), for table filtering. */
export function relevantWindZonesForLoadCase(loadCase) {
  const set = new Set();
  const d = loadCase?.detail || {};
  const axisX = loadCase?.axis === 'X' || loadCase?.id === 'wind_long';

  (d.wall_parts || []).forEach((p) => {
    if (p?.zone) set.add(String(p.zone).toUpperCase());
  });
  (d.roof_zones_per_seg || []).forEach((seg) => {
    (seg.parts || []).forEach((p) => {
      if (p?.zone) set.add(String(p.zone).toUpperCase());
    });
  });
  (d.roof_parts || []).forEach((p) => {
    if (p?.zone) set.add(String(p.zone).toUpperCase());
  });

  // Applied wall zones (avoid listing unused A/B/C reference rows for Wind X).
  const wz = d.wall_zones || [];
  if (axisX) {
    if (!(d.wall_parts || []).length && wz[0]?.zone) {
      set.add(String(wz[0].zone).toUpperCase());
    }
  } else {
    wz.forEach((w) => {
      if (w?.zone) set.add(String(w.zone).toUpperCase());
    });
  }

  if (!set.size) {
    if (axisX) {
      ['A', 'F', 'G', 'H', 'I'].forEach((z) => set.add(z));
    } else {
      ['D', 'E', 'F', 'G', 'H', 'I', 'J'].forEach((z) => set.add(z));
    }
  }
  return set;
}

function zoneSurfaceMatchesAxis(surface, axis) {
  const s = String(surface || '');
  if (axis === 'X') {
    if (s.includes('Wind Y')) return false;
    if (['Side wall', 'Windward wall', 'Leeward wall'].some((t) => s.includes(t))) {
      return /Side wall/i.test(s);
    }
    return s.includes('Wind X') || /Side wall/i.test(s);
  }
  // Wind Y
  if (s.includes('Wind X')) return false;
  if (/Side wall/i.test(s) && !s.includes('Windward') && !s.includes('Leeward')) return false;
  return true;
}

export function buildWindCaseRows(loadCase) {
  if (!loadCase || loadCase.kind !== 'wind') return [];
  const d = loadCase.detail || {};
  const walls = wallZoneLettersForLoadCase(loadCase);
  const rows = [];
  if (d.qp_kN_m2 != null) rows.push({ label: 'q_p (kN/m²)', value: fmtNum(d.qp_kN_m2, 4) });
  if (d.c_pi != null) rows.push({ label: 'c_pi (critical)', value: fmtNum(d.c_pi, 3) });
  if (d.c_pi_pick?.c_pi_alt != null) {
    rows.push({ label: 'c_pi (not used)', value: fmtNum(d.c_pi_pick.c_pi_alt, 3) });
  }
  if (d.c_pi_pick?.reason) rows.push({ label: 'c_pi selection', value: String(d.c_pi_pick.reason) });
  if (d.cpe_net_wall_left != null) {
    rows.push({ label: `Net c_pe zone ${walls.left}`, value: fmtNum(d.cpe_net_wall_left, 3) });
  }
  if (d.cpe_net_wall_right != null) {
    rows.push({ label: `Net c_pe zone ${walls.right}`, value: fmtNum(d.cpe_net_wall_right, 3) });
  }
  rows.push({ label: `Column q zone ${walls.left} (kN/m)`, value: fmtNum(loadCase.colLeftQ, 3) });
  rows.push({ label: `Column q zone ${walls.right} (kN/m)`, value: fmtNum(loadCase.colRightQ, 3) });
  if (d.tributary_m != null) rows.push({ label: 'Tributary (m)', value: fmtNum(d.tributary_m, 2) });
  return rows;
}

export function buildWindZonesTableRows(loadCase, windMeta) {
  const table = loadCase?.detail?.zones_table || windMeta?.zones_table || [];
  if (!Array.isArray(table) || !table.length) return [];
  const axis = loadCase?.axis || (loadCase?.id === 'wind_long' ? 'X' : 'Y');
  const relevant = relevantWindZonesForLoadCase(loadCase);
  return table
    .filter((r) => {
      const z = String(r.zone || '').toUpperCase();
      if (!relevant.has(z)) return false;
      return zoneSurfaceMatchesAxis(r.surface, axis);
    })
    .map((r) => ({
      zone: r.zone,
      surface: r.surface || '—',
      extent: r.extent || '—',
      cpe: r.cpe != null ? fmtNum(r.cpe, 3) : '—',
      length: r.length_m != null ? fmtNum(r.length_m, 2) : '—',
    }));
}

export function buildRoofZoneSegmentRows(loadCase) {
  const segs = loadCase?.detail?.roof_zones_per_seg;
  if (!Array.isArray(segs) || !segs.length) return [];
  return segs.map((r) => {
    const letters = (r.parts || [])
      .map((p) => String(p.zone || '').toUpperCase())
      .filter(Boolean);
    const uniq = [...new Set(letters)];
    const zones = uniq.length
      ? uniq.map((z) => {
          const part = (r.parts || []).find((p) => String(p.zone || '').toUpperCase() === z);
          const len = part?.length_m != null ? ` (${fmtNum(part.length_m, 2)}m)` : '';
          return `${z}${len}`;
        }).join(' + ')
      : (r.debug || '—');
    return {
      seg: r.seg,
      x0: fmtNum(r.x0, 2),
      x1: fmtNum(r.x1, 2),
      zones,
      cpeExt: r.cpe_ext != null ? fmtNum(r.cpe_ext, 3) : '—',
      cpeNet: r.cpe_net != null ? fmtNum(r.cpe_net, 3) : '—',
      w: (() => {
        const idx = (r.seg || 1) - 1;
        const q = loadCase.roofSegQ?.[idx];
        return q != null ? fmtNum(q, 3) : '—';
      })(),
    };
  });
}

/**
 * Apply per-frame zone loads onto a characteristic wind case for display.
 */
export function applyFrameZoneToLoadCase(loadCase, frameIndex) {
  if (!loadCase) return null;
  const zones = loadCase.frameZones || loadCase.detail?.frame_zones || [];
  if (!zones.length || frameIndex == null || frameIndex < 0 || frameIndex >= zones.length) {
    return {
      ...loadCase,
      frameIndex: null,
      frameDebug: null,
    };
  }
  const fz = zones[frameIndex];
  let roofSegQ = loadCase.roofSegQ || [];
  if (Array.isArray(fz.roof_top_k_per_seg_k) && fz.roof_top_k_per_seg_k.length) {
    roofSegQ = fz.roof_top_k_per_seg_k.map(Number);
  } else if (fz.roof_scale != null && Number.isFinite(Number(fz.roof_scale))) {
    const s = Number(fz.roof_scale);
    roofSegQ = roofSegQ.map((q) => Number(q) * s);
  }
  const detail = {
    ...(loadCase.detail || {}),
    ...(fz.roof_zones_per_seg ? { roof_zones_per_seg: fz.roof_zones_per_seg } : {}),
    ...(fz.wall_parts ? { wall_parts: fz.wall_parts } : {}),
    ...(fz.roof_parts ? { roof_parts: fz.roof_parts } : {}),
  };
  // Wind X frames often have length-weighted roof_parts but no per-seg roof zone list.
  if (!detail.roof_zones_per_seg?.length && Array.isArray(fz.roof_parts) && fz.roof_parts.length && roofSegQ.length) {
    detail.roof_zones_per_seg = roofSegQ.map((_, i) => ({
      seg: i + 1,
      parts: fz.roof_parts,
      cpe_net: fz.cpe_net_roof,
      debug: fz.roof_debug || '',
    }));
  }
  // Wind Y keeps D/E on walls; Wind X uses length-weighted A/B/C from the strip.
  if (Array.isArray(fz.wall_parts) && fz.wall_parts.length && (loadCase.axis === 'X' || loadCase.id === 'wind_long')) {
    const letters = [];
    fz.wall_parts.forEach((p) => {
      const z = String(p.zone || '').toUpperCase();
      if (z && !letters.includes(z)) letters.push(z);
    });
    if (letters.length) {
      detail.wall_zones = letters.map((zone) => ({ zone, face: 'side walls' }));
    }
  }
  const short = loadCase.shortLabel || loadCase.label || 'Wind';
  return {
    ...loadCase,
    detail,
    roofSegQ,
    colLeftQ: Number(fz.col_left_q_kN_m) || 0,
    colRightQ: Number(fz.col_right_q_kN_m) || 0,
    frameIndex: Number(fz.frame ?? frameIndex),
    frameDebug: fz.debug || fz.wall_debug || null,
    regionLabels: (detail.roof_zones_per_seg || []).map((r) => r.debug || ''),
    label: `${short} · Frame ${fz.frame ?? frameIndex}`,
  };
}

export function buildFrameZoneRows(loadCase) {
  const frames = loadCase?.detail?.frame_zones || loadCase?.frameZones;
  if (!Array.isArray(frames) || !frames.length) return [];
  return frames.map((f) => ({
    frame: f.frame,
    y0: fmtNum(f.y0, 2),
    y1: fmtNum(f.y1, 2),
    trib: fmtNum(f.tributary_m, 2),
    wallZones: f.wall_debug || '—',
    roofZones: f.roof_debug || '—',
    qLeft: fmtNum(f.col_left_q_kN_m, 3),
    qRight: fmtNum(f.col_right_q_kN_m, 3),
    debug: f.debug || f.wall_debug || '—',
  }));
}

export function buildSnowSegmentRows(loadCase, geometry) {
  if (!loadCase || (loadCase.id !== 'snow' && loadCase.id !== 'snow_case_ii' && !String(loadCase.id).startsWith('snow_'))) {
    return [];
  }
  const { nodes } = topChordSegments(geometry);
  const sm = loadCase.detail || {};
  const wSeg = loadCase.roofSegQ || [];
  const mu = sm.mu_per_seg || [];
  const sSeg = sm.s_kN_m2_per_seg || [];
  const sides = sm.side_per_seg || loadCase.regionLabels || [];
  return wSeg.map((w, i) => ({
    seg: i + 1,
    x0: nodes[i] ? fmtNum(nodes[i].x, 2) : '—',
    x1: nodes[i + 1] ? fmtNum(nodes[i + 1].x, 2) : '—',
    side: sides[i] || '—',
    mu: mu[i] != null ? fmtNum(mu[i], 3) : '—',
    s: sSeg[i] != null ? fmtNum(sSeg[i], 3) : '—',
    w: fmtNum(w, 3),
  }));
}

export function buildRoofSegmentRows(loadCase, geometry) {
  const { nodes } = topChordSegments(geometry);
  const segs = loadCase?.roofSegQ || [];
  const labels = loadCase?.regionLabels || [];
  return segs.map((w, i) => ({
    seg: i + 1,
    x0: nodes[i] ? fmtNum(nodes[i].x, 2) : '—',
    x1: nodes[i + 1] ? fmtNum(nodes[i + 1].x, 2) : '—',
    zones: labels[i] || '—',
    w: fmtNum(w, 3),
  }));
}

export function buildBottomSegmentRows(loadCase, geometry) {
  const { nodes } = bottomChordSegments(geometry);
  const segs = loadCase?.bottomSegQ || [];
  return segs.map((w, i) => ({
    seg: i + 1,
    x0: nodes[i] ? fmtNum(nodes[i].x, 2) : '—',
    x1: nodes[i + 1] ? fmtNum(nodes[i + 1].x, 2) : '—',
    w: fmtNum(w, 3),
  }));
}

function escapeXml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function windZoneColor(zoneKey, q) {
  const z = String(zoneKey || '').replace(/[^A-Z]/g, '').charAt(0);
  if (z === 'A') return Number(q) >= 0 ? '#e65100' : '#6a1b9a';
  if (z === 'B') return '#1565c0';
  if (z === 'C') return '#00695c';
  return Number(q) >= 0 ? '#e65100' : '#1565c0';
}

function zoneKeyForSeg(zoneSegs, i) {
  const parts = zoneSegs?.[i]?.parts;
  if (Array.isArray(parts) && parts.length) {
    return parts.map((p) => String(p.zone || '').toUpperCase()).filter(Boolean).join('+') || '';
  }
  return '';
}

function groupLoadRegions(segs, { tolRel = 0.04, zoneSegs = null } = {}) {
  const regions = [];
  let i = 0;
  while (i < segs.length) {
    const q0 = Number(segs[i]) || 0;
    if (Math.abs(q0) < 1e-9) {
      i += 1;
      continue;
    }
    const z0 = zoneKeyForSeg(zoneSegs, i);
    let j = i + 1;
    while (j < segs.length) {
      const qj = Number(segs[j]) || 0;
      if (Math.abs(qj) < 1e-9) break;
      if (Math.sign(qj) !== Math.sign(q0)) break;
      if (zoneSegs && zoneKeyForSeg(zoneSegs, j) !== z0) break;
      const ref = Math.max(Math.abs(q0), Math.abs(qj), 1e-9);
      if (Math.abs(qj - q0) / ref > tolRel) break;
      j += 1;
    }
    regions.push({ i0: i, i1: j - 1, q: q0, zoneKey: z0 || null });
    i = j;
  }
  return regions;
}

function roofZoneLabel(zoneSegs, i0, i1) {
  if (!zoneSegs?.length) return null;
  const letters = [];
  for (let i = i0; i <= i1; i += 1) {
    const key = zoneKeyForSeg(zoneSegs, i);
    if (!key) continue;
    key.split('+').forEach((z) => {
      if (z && !letters.includes(z)) letters.push(z);
    });
  }
  return letters.length ? letters.join('+') : null;
}

function appendRoofLoadPolygon(parts, {
  topNodes, i0, i1, q, color, markerId, tx, ty, ref, tipGap, bandH, label,
}) {
  const pts = [];
  for (let i = i0; i <= i1 + 1; i += 1) pts.push(topNodes[i]);
  if (pts.length < 2) return;

  const towardMember = q >= 0;
  const inner = tipGap;
  const outer = tipGap + bandH;
  const offsetAt = (i, dist) => {
    const n = pts.length;
    const norm = (i0, i1) => segmentOutwardNormal(pts[i0].x, pts[i0].y, pts[i1].x, pts[i1].y);
    let nx;
    let ny;
    let scale = 1;
    if (i <= 0) {
      ({ nx, ny } = norm(0, 1));
    } else if (i >= n - 1) {
      ({ nx, ny } = norm(n - 2, n - 1));
    } else {
      const a = norm(i - 1, i);
      const b = norm(i, i + 1);
      nx = a.nx + b.nx;
      ny = a.ny + b.ny;
      const len = Math.hypot(nx, ny) || 1;
      nx /= len;
      ny /= len;
      const align = Math.max(0.55, nx * a.nx + ny * a.ny);
      scale = Math.min(1.6, 1 / align);
    }
    return { x: pts[i].x + nx * dist * scale, y: pts[i].y + ny * dist * scale };
  };
  const outerPts = pts.map((_, i) => offsetAt(i, outer));
  const innerPts = pts.map((_, i) => offsetAt(i, inner));

  const poly = [...outerPts, ...innerPts.slice().reverse()];
  const polyStr = poly.map((p) => `${tx(p.x).toFixed(2)},${ty(p.y).toFixed(2)}`).join(' ');
  parts.push(
    `<polygon points="${polyStr}" fill="${color}" fill-opacity="0.08" stroke="${color}" stroke-width="1.4"/>`,
  );

  let totalLen = 0;
  const segLens = [];
  for (let i = 0; i < pts.length - 1; i += 1) {
    const L = Math.hypot(pts[i + 1].x - pts[i].x, pts[i + 1].y - pts[i].y);
    segLens.push(L);
    totalLen += L;
  }
  const spacing = Math.max(ref * 0.045, totalLen / 18);
  const nArrows = Math.max(3, Math.min(40, Math.round(totalLen / spacing)));

  for (let k = 0; k < nArrows; k += 1) {
    const sTarget = ((k + 0.5) / nArrows) * totalLen;
    let rem = sTarget;
    let si = 0;
    while (si < segLens.length - 1 && rem > segLens[si]) {
      rem -= segLens[si];
      si += 1;
    }
    const a = pts[si];
    const b = pts[si + 1];
    const L = segLens[si] || 1;
    const t = rem / L;
    const mx = a.x + t * (b.x - a.x);
    const my = a.y + t * (b.y - a.y);
    const { nx, ny } = segmentOutwardNormal(a.x, a.y, b.x, b.y);
    const startOff = towardMember ? outer - 0.02 * ref : inner + 0.02 * ref;
    const endOff = towardMember ? inner : outer;
    const x0 = mx + nx * startOff;
    const y0 = my + ny * startOff;
    const x1 = mx + nx * endOff;
    const y1 = my + ny * endOff;
    parts.push(
      `<line x1="${tx(x0)}" y1="${ty(y0)}" x2="${tx(x1)}" y2="${ty(y1)}" stroke="${color}" stroke-width="1.15" marker-end="url(#${markerId})"/>`,
    );
  }

  const midSeg = Math.floor((pts.length - 1) / 2);
  const a = pts[midSeg];
  const b = pts[midSeg + 1] || pts[pts.length - 1];
  const { nx, ny } = segmentOutwardNormal(a.x, a.y, b.x, b.y);
  const lx = 0.5 * (a.x + b.x) + nx * (outer + 0.018 * ref);
  const ly = 0.5 * (a.y + b.y) + ny * (outer + 0.018 * ref);
  const txt = label != null ? label : fmtNum(q, 2);
  parts.push(
    `<text x="${tx(lx)}" y="${ty(ly)}" font-size="13" fill="${color}" text-anchor="middle" font-weight="600">${escapeXml(String(txt))}</text>`,
  );
}

function appendColumnLoadPolygon(parts, {
  nodes, q, side, color, markerId, tx, ty, ref, tipGap, bandH, zoneLabel,
}) {
  if (!nodes.length || Math.abs(q) < 1e-9) return;
  const sorted = [...nodes].sort((a, b) => a.y - b.y);
  const yBot = sorted[0].y;
  const yTop = sorted[sorted.length - 1].y;
  const xCol = sorted.reduce((s, n) => s + n.x, 0) / sorted.length;
  const outward = side === 'L' ? -1 : 1;
  const xInner = xCol + outward * tipGap;
  const xOuter = xCol + outward * (tipGap + bandH);

  const poly = [
    { x: xOuter, y: yBot },
    { x: xOuter, y: yTop },
    { x: xInner, y: yTop },
    { x: xInner, y: yBot },
  ];
  const polyStr = poly.map((p) => `${tx(p.x).toFixed(2)},${ty(p.y).toFixed(2)}`).join(' ');
  parts.push(
    `<polygon points="${polyStr}" fill="${color}" fill-opacity="0.08" stroke="${color}" stroke-width="1.4"/>`,
  );

  // q = global frame Fx (+ to the right / +span). Arrows show force on the structure.
  const forceDir = q >= 0 ? 1 : -1;
  const h = Math.abs(yTop - yBot) || ref * 0.3;
  const nArrows = Math.max(4, Math.min(28, Math.round(h / Math.max(ref * 0.04, 1e-6))));
  for (let k = 0; k < nArrows; k += 1) {
    const t = (k + 0.5) / nArrows;
    const y = yBot + t * (yTop - yBot);
    // Place shaft in the outside band; tip points in forceDir.
    const xStart = forceDir > 0
      ? Math.min(xInner, xOuter) + 0.02 * ref
      : Math.max(xInner, xOuter) - 0.02 * ref;
    const xEnd = forceDir > 0
      ? Math.max(xInner, xOuter) - 0.02 * ref
      : Math.min(xInner, xOuter) + 0.02 * ref;
    parts.push(
      `<line x1="${tx(xStart)}" y1="${ty(y)}" x2="${tx(xEnd)}" y2="${ty(y)}" stroke="${color}" stroke-width="1.15" marker-end="url(#${markerId})"/>`,
    );
  }

  const lx = xOuter + outward * 0.02 * ref;
  const ly = 0.5 * (yBot + yTop);
  const tag = zoneLabel || side;
  parts.push(
    `<text x="${tx(lx)}" y="${ty(ly)}" font-size="13" fill="${color}" text-anchor="${side === 'L' ? 'end' : 'start'}" font-weight="600">${escapeXml(String(tag))} ${fmtNum(q, 2)}</text>`,
  );
}

/**
 * Build inline SVG for characteristic loads on frame elevation.
 * Roof/columns: closed load polygons filled with regular arrow hatching.
 */
export function buildLoadingsSvgString(geometry, loadCase, { width = 1100 } = {}) {
  if (!geometry?.nodes?.length || !loadCase) {
    return '<svg xmlns="http://www.w3.org/2000/svg" width="400" height="80"><text x="8" y="24" font-size="12">No geometry</text></svg>';
  }
  const { minX, maxX, minY, maxY, w, h } = boundsFromGeometry(geometry);
  const W = width;
  const worldAR = h / Math.max(w, 1e-6);
  let H = Math.round(W * worldAR * 1.05);
  H = Math.min(820, Math.max(460, H));
  const sc = Math.min(W / w, H / h);
  const ox = (W - (maxX - minX) * sc) / 2;
  const oy = (H - (maxY - minY) * sc) / 2;
  const tx = (x) => ox + (x - minX) * sc;
  const ty = (y) => oy + (maxY - y) * sc;
  const ref = Math.max(maxX - minX, maxY - minY, 1);
  const tipGap = ref * 0.012;
  const arrowLen = ref * 0.028;

  const roofColor = loadCase.kind === 'wind' ? '#1565c0' : '#2e7d32';
  const colColor = '#c62828';
  const markerRoof = 'ldArrRoof';
  const markerCol = 'ldArrCol';

  const parts = [];
  parts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" width="100%" viewBox="0 0 ${W} ${H}" role="img">`,
    `<defs>
      <marker id="${markerRoof}" markerWidth="7" markerHeight="7" refX="6" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 z" fill="${roofColor}"/></marker>
      <marker id="ldArrBot" markerWidth="7" markerHeight="7" refX="6" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 z" fill="#1565c0"/></marker>
      <marker id="ldWindPos" markerWidth="7" markerHeight="7" refX="6" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 z" fill="#e65100"/></marker>
      <marker id="ldWindAneg" markerWidth="7" markerHeight="7" refX="6" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 z" fill="#6a1b9a"/></marker>
      <marker id="ldWindB" markerWidth="7" markerHeight="7" refX="6" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 z" fill="#1565c0"/></marker>
      <marker id="ldWindC" markerWidth="7" markerHeight="7" refX="6" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 z" fill="#00695c"/></marker>
      <marker id="${markerCol}" markerWidth="7" markerHeight="7" refX="6" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 z" fill="${colColor}"/></marker>
    </defs>`,
  );

  (geometry.members || []).forEach((m) => {
    const a = geometry.nodes[m.i];
    const b = geometry.nodes[m.j];
    if (!a || !b) return;
    const col = m.group === 'column' || m.group === 'column_outer' || m.group === 'arch_leg'
      ? '#546e7a'
      : m.group === 'arch_rib'
        ? '#b71c1c'
        : '#37474f';
    const sw = m.group === 'column' || m.group === 'column_outer' || m.group === 'arch_rib' || m.group === 'arch_leg' ? 2.8 : 2.1;
    parts.push(
      `<line x1="${tx(a.x)}" y1="${ty(a.y)}" x2="${tx(b.x)}" y2="${ty(b.y)}" stroke="${col}" stroke-width="${sw}" stroke-linecap="round"/>`,
    );
  });

  const { nodes: topNodes } = topChordSegments(geometry);
  const segs = loadCase.roofSegQ || [];
  const qAbsMax = Math.max(...segs.map((q) => Math.abs(Number(q) || 0)), 1e-6);
  const zoneSegs = loadCase.kind === 'wind' ? loadCase.detail?.roof_zones_per_seg : null;
  const regions = groupLoadRegions(segs, { zoneSegs });

  regions.forEach((reg) => {
    const bandH = tipGap + arrowLen * 0.35 + (Math.abs(reg.q) / qAbsMax) * ref * 0.055;
    let label = fmtNum(reg.q, 2);
    if (loadCase.kind === 'wind') {
      const z = reg.zoneKey || roofZoneLabel(zoneSegs, reg.i0, reg.i1);
      if (z) label = `${z} ${fmtNum(reg.q, 2)}`;
    } else if (loadCase.regionLabels?.[reg.i0] && reg.i0 === reg.i1) {
      const rl = loadCase.regionLabels[reg.i0];
      if (rl && !String(rl).startsWith('zones ')) label = `${rl} ${fmtNum(reg.q, 2)}`;
    }
    const zoneColor = loadCase.kind === 'wind' ? windZoneColor(reg.zoneKey, reg.q) : roofColor;
    const zoneMarker = loadCase.kind === 'wind'
      ? (zoneColor === '#e65100'
        ? 'ldWindPos'
        : zoneColor === '#6a1b9a'
          ? 'ldWindAneg'
          : zoneColor === '#00695c'
            ? 'ldWindC'
            : 'ldWindB')
      : markerRoof;
    appendRoofLoadPolygon(parts, {
      topNodes,
      i0: reg.i0,
      i1: reg.i1,
      q: reg.q,
      color: zoneColor,
      markerId: zoneMarker,
      tx,
      ty,
      ref,
      tipGap,
      bandH,
      label,
    });
  });

  if (loadCase.kind === 'bottom') {
    const { nodes: botNodes } = bottomChordSegments(geometry);
    const botSegs = loadCase.bottomSegQ || [];
    const botColor = '#1565c0';
    const qAbsMaxBot = Math.max(...botSegs.map((q) => Math.abs(Number(q) || 0)), 1e-6);
    groupLoadRegions(botSegs).forEach((reg) => {
      const bandH = tipGap + arrowLen * 0.35 + (Math.abs(reg.q) / qAbsMaxBot) * ref * 0.055;
      appendRoofLoadPolygon(parts, {
        topNodes: botNodes,
        i0: reg.i0,
        i1: reg.i1,
        q: reg.q,
        color: botColor,
        markerId: 'ldArrBot',
        tx,
        ty,
        ref,
        tipGap,
        bandH,
        label: fmtNum(reg.q, 2),
      });
    });
  }

  if (loadCase.kind === 'wind') {
    const walls = wallZoneLettersForLoadCase(loadCase);
    const colMembers = (geometry.members || []).filter((m) =>
      m.group === 'column' || m.group === 'column_outer' || m.group === 'arch_leg',
    );
    if (colMembers.length) {
      const byX = {};
      colMembers.forEach((m) => {
        const a = geometry.nodes[m.i];
        const b = geometry.nodes[m.j];
        if (!a || !b) return;
        const key = ((a.x + b.x) / 2).toFixed(3);
        if (!byX[key]) byX[key] = [];
        byX[key].push(a, b);
      });
      const keys = Object.keys(byX).sort((ka, kb) => Number(ka) - Number(kb));
      const bandH = tipGap + arrowLen * 0.5 + ref * 0.035;
      if (keys[0] && Math.abs(loadCase.colLeftQ) > 1e-9) {
        appendColumnLoadPolygon(parts, {
          nodes: byX[keys[0]],
          q: loadCase.colLeftQ,
          side: 'L',
          color: colColor,
          markerId: markerCol,
          tx,
          ty,
          ref,
          tipGap,
          bandH,
          zoneLabel: walls.left,
        });
      }
      const rightKey = keys[keys.length - 1];
      if (rightKey && Math.abs(loadCase.colRightQ) > 1e-9) {
        appendColumnLoadPolygon(parts, {
          nodes: byX[rightKey],
          q: loadCase.colRightQ,
          side: 'R',
          color: colColor,
          markerId: markerCol,
          tx,
          ty,
          ref,
          tipGap,
          bandH,
          zoneLabel: walls.right,
        });
      }
    }
  }

  const caption = escapeXml(loadCase.label || '');
  parts.push(`<text x="12" y="22" font-size="15" fill="#333" font-weight="600">${caption}</text>`);
  parts.push('</svg>');
  return parts.join('');
}

export { fmtNum };
